'use client'
import { useMemo } from 'react'
import clsx from 'clsx'

import { RaContainer, RaHeading, RaPaper, RaTypography } from '@components/core/index.core'
import { RaButtonAddToCart } from '@components/features/RaButtonAddToCart/RaButtonAddToCart'
import { RaButtonAddToWishlist } from '@components/features/RaButtonAddToWishlist/RaButtonAddToWishlist'
import { BuildingBlockPostType, RoundtripPostType } from '@typings'
import { unescapeHTML } from '@utilities/helpers/unescape-html.helpers'
import { useScrollPosition } from '@utilities/hooks/useScrollPosition.hooks'

import styles from './ra-product-sticky-footer.module.scss'

const SCROLL_THRESHOLD = 1000

interface Props {
  /**
   * The product item to display in the sticky footer.
   * Can be either a building block or roundtrip post type.
   */
  item: BuildingBlockPostType | RoundtripPostType
}

/**
 * RaProductStickyFooter Component
 *
 * @description A sticky footer component that appears at the bottom of the screen when scrolling past a threshold.
 * Displays the product title with its travel time and starting price underneath, and the add-to-cart
 * button for easy access during product browsing.
 */
export const RaProductStickyFooter = ({ item }: Props) => {
  const { durationText, startingPriceText: priceText } = item
  const { isAboveThreshold } = useScrollPosition({
    threshold: SCROLL_THRESHOLD,
  })

  const classes = useMemo(() => {
    return clsx(
      styles['ra-product-sticky-footer'],
      isAboveThreshold && styles['ra-product-sticky-footer--active'],
    )
  }, [isAboveThreshold])

  return (
    <div className={classes}>
      <RaPaper>
        <RaContainer>
          <div className={styles['ra-product-sticky-footer__content']}>
            <div className={styles['ra-product-sticky-footer__content__text']}>
              <RaHeading
                component="h4"
                noPadding>
                {unescapeHTML(item.title.rendered)}
              </RaHeading>
              {(durationText || priceText) && (
                <div className={styles['ra-product-sticky-footer__content__meta']}>
                  {durationText && (
                    <RaTypography
                      component="span"
                      size="s"
                      customClasses={styles['ra-product-sticky-footer__content__duration']}>
                      {durationText}
                    </RaTypography>
                  )}
                  {durationText && priceText && (
                    <RaTypography
                      component="span"
                      size="s"
                      aria-hidden="true">
                      ·
                    </RaTypography>
                  )}
                  {priceText && (
                    <RaTypography
                      component="span"
                      size="s"
                      weight="semibold">
                      {priceText}
                    </RaTypography>
                  )}
                </div>
              )}
            </div>
            <div className={styles['ra-product-sticky-footer__content__buttons']}>
              <RaButtonAddToWishlist
                product={item}
                variant="outlined"
              />
              <RaButtonAddToCart
                item={item}
                position="sticky"
              />
            </div>
          </div>
        </RaContainer>
      </RaPaper>
    </div>
  )
}
